import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar, Breadcrumb, Card, Col, Row, Select, Spin, Table, Tabs, Tag,
  Typography, message, Space, Statistic,
} from 'antd';
import {
  UserOutlined, CrownOutlined, TeamOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAdminUserReferralsApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

const { Title, Text } = Typography;

const SUBSCRIBED_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: 'Subscribed' },
  { value: '0', label: 'Not Subscribed' },
];

function ReferralsTab({ userId }) {
  const [loading, setLoading]     = useState(false);
  const [promoter, setPromoter]   = useState(null);
  const [summary, setSummary]     = useState(null);
  const [data, setData]           = useState([]);
  const [meta, setMeta]           = useState({ total: 0, current_page: 1, per_page: 20 });
  const [subscribed, setSubscribed] = useState('');

  const fetchReferrals = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        user_id: userId,
        sort_by: 'referred_at',
        sort_dir: 'desc',
        per_page: meta.per_page,
        page,
      };
      if (subscribed !== '') params.subscribed = subscribed;
      const res = await getAdminUserReferralsApi(params);
      const body = res.data;
      setPromoter(body.promoter || null);
      setSummary(body.summary || null);
      setData(body.data || []);
      setMeta(body.meta || meta);
    } catch {
      message.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [userId, subscribed, meta.per_page]);

  useEffect(() => { fetchReferrals(1); }, [subscribed]);

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar icon={<UserOutlined />} size={32} style={{ background: '#e8f5e9', color: '#1a6b2a' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.user_name || '-'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.user_mobile || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, r) => r.is_subscribed
        ? <Tag color="gold" icon={<CrownOutlined />}>Prime</Tag>
        : <Tag color="default">Free</Tag>,
    },
    {
      title: 'Expires',
      dataIndex: 'subscription_expire',
      width: 120,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'Referred At',
      dataIndex: 'referred_at',
      width: 140,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
  ];

  return (
    <div>
      {summary && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {[
            { label: 'Total Referred',      value: summary.total_referred,       color: '#1a6b2a', bg: '#f6ffed' },
            { label: 'Subscribed',          value: summary.total_subscribed,     color: '#d4a017', bg: '#fffbe6' },
            { label: 'Not Subscribed',      value: summary.total_not_subscribed, color: '#9ca3af', bg: '#fafafa' },
          ].map((item) => (
            <Col xs={24} sm={8} key={item.label}>
              <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                <Statistic title={item.label} value={item.value} valueStyle={{ color: item.color, fontSize: 22, fontWeight: 'bold' }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {promoter?.share_ref_code && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Referral code: </Text>
          <Tag color="green">{promoter.share_ref_code}</Tag>
        </div>
      )}

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Select
          value={subscribed}
          onChange={setSubscribed}
          options={SUBSCRIBED_OPTIONS}
          style={{ width: 150 }}
          size="small"
        />
      </div>

      <Table
        rowKey="installed_user_id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="small"
        pagination={{
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
          showSizeChanger: false,
          showTotal: (t) => `${t} referrals`,
          onChange: (page) => fetchReferrals(page),
        }}
      />
    </div>
  );
}

function SubscriptionsTab() {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
      <CrownOutlined style={{ fontSize: 32, marginBottom: 8 }} />
      <div>Subscription history coming soon</div>
    </div>
  );
}

function CropsTab() {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
      <AppstoreOutlined style={{ fontSize: 32, marginBottom: 8 }} />
      <div>Crops data coming soon</div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();
  const user       = location.state?.user || null;

  const tabItems = [
    {
      key: 'referrals',
      label: (
        <span><TeamOutlined /> Referrer Users</span>
      ),
      children: <ReferralsTab userId={userId} />,
    },
    {
      key: 'subscriptions',
      label: (
        <span><CrownOutlined /> Subscriptions</span>
      ),
      children: <SubscriptionsTab />,
    },
    {
      key: 'crops',
      label: (
        <span><AppstoreOutlined /> Crops</span>
      ),
      children: <CropsTab />,
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <span style={{ cursor: 'pointer' }} onClick={() => navigate('/admin/users')}>Users</span> },
          { title: user?.name || `User #${userId}` },
        ]}
      />

      {user && (
        <Card style={{ borderRadius: 10, marginBottom: 20 }}>
          <Space size={16}>
            <Avatar
              src={user.photo ? USER_IMAGE(user.photo) : null}
              icon={<UserOutlined />}
              size={56}
              style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }}
            />
            <div>
              <Title level={4} style={{ margin: 0 }}>{user.name}</Title>
              <Text type="secondary">{user.country_code} {user.mobile}</Text>
              <div style={{ marginTop: 4 }}>
                {user.isPrime
                  ? <Tag color="gold" icon={<CrownOutlined />}>Prime</Tag>
                  : <Tag color="default">Free</Tag>}
                {user.expireOn && (
                  <Text style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
                    Expires {formatDate(user.expireOn)}
                  </Text>
                )}
              </div>
            </div>
          </Space>
        </Card>
      )}

      <Card style={{ borderRadius: 10 }}>
        <Tabs defaultActiveKey="referrals" items={tabItems} />
      </Card>
    </div>
  );
}
